import { VariableBenefitTargetPayment } from '../../../../../models/submissions/variable-benefit-target-payment';
import { VariableBenefitTargetPaymentRequest } from '../../../../../models/submissions/requests/variable-benefit-target-payment-request';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { NumberUtils } from '../../../../../utils/number-utils';
import { Alert } from '../../../../../models/alerts/dto/alert';
import { buildYellowBanner } from './variable-benefit-payment-step-utils';

export interface VbTargetSplitYearCapacity {
  governingJurisdictionId: number | null;
  availableThisYear: number;
  askedThisYear: number;
  paymentsLeft: number;
  paymentsInFull: number;
}

export interface VbTargetSplitWarnings {
  belowMinimum: boolean;
  totalMinimum: number;
  placed: number;
  unplaced: number;
  yearCapacity: VbTargetSplitYearCapacity | null;
}

export const NO_VB_TARGET_SPLIT_WARNINGS: VbTargetSplitWarnings = {
  belowMinimum: false,
  totalMinimum: 0,
  placed: 0,
  unplaced: 0,
  yearCapacity: null
};

// What the internal portal warns about once a split is accepted. A refused split has its refusal instead, and a change
// starting in a later year is split on this year's limits only as a guide
export function vbTargetSplitWarnings(
  request: VariableBenefitTargetPaymentRequest,
  split: VariableBenefitTargetPayment
): VbTargetSplitWarnings {
  if (split.refusal !== null || split.startsInALaterYear) return NO_VB_TARGET_SPLIT_WARNINGS;

  return {
    belowMinimum: split.belowMinimum,
    totalMinimum: split.totalMinimum,
    placed: split.placed,
    unplaced: split.unplaced > 0 ? split.unplaced : 0,
    yearCapacity: vbTargetSplitYearCapacity(request, split)
  };
}

// The backend lets the uncapped account give its whole balance to each payment, so a monthly split can ask it for more
// over the year than it holds. Payments left are counted as the backend counts them, from the start month to December.
// Nothing shows at 0 in full: the year then holds less than one payment, so the split leaves part of the target
// unplaced and the target banner says so
function vbTargetSplitYearCapacity(
  request: VariableBenefitTargetPaymentRequest,
  split: VariableBenefitTargetPayment
): VbTargetSplitYearCapacity | null {
  if (request.recurringPaymentFrequencyId !== VariableBenefitPaymentFrequencyEnum.Monthly) return null;

  const paymentsLeft = 13 - Number(request.recurringPaymentStartDate.split('-')[1]);
  const runningPast = split.accounts.find(
    account => account.uncapped && account.recurringPaymentAmount * paymentsLeft > account.ceiling
  );
  if (!runningPast) return null;

  const availableThisYear = NumberUtils.round(
    split.accounts.reduce(
      (sum, account) => sum + (account.uncapped ? account.ceiling : account.ceiling * paymentsLeft),
      0
    )
  );
  const paymentsInFull = Math.floor(availableThisYear / split.targetRecurringPaymentAmount);
  if (paymentsInFull === 0) return null;

  return {
    governingJurisdictionId: runningPast.governingJurisdictionId,
    availableThisYear,
    askedThisYear: NumberUtils.round(split.targetRecurringPaymentAmount * paymentsLeft),
    paymentsLeft,
    paymentsInFull
  };
}

// One banner covers the target's own warnings. It stays while the split does, so it can't be dismissed
export function buildVbTargetSplitWarningBanner(
  warnings: VbTargetSplitWarnings,
  formatAmount: (amount: number) => string
): Alert | null {
  const descriptions = [
    warnings.belowMinimum
      ? $localize`This target payment is less than the accounts' combined minimum of ${formatAmount(
          warnings.totalMinimum
        )} a payment. Each account's minimum is filled in the drawdown order until the target runs out.`
      : null,
    warnings.unplaced > 0
      ? $localize`The accounts can take ${formatAmount(
          warnings.placed
        )} of this target payment. The other ${formatAmount(warnings.unplaced)} a payment can't be placed.`
      : null
  ].filter((description): description is string => description !== null);
  if (!descriptions.length) return null;

  return buildYellowBanner($localize`Check the Target Payment`, descriptions.join(' '), false);
}
